import getBreadcrumbContentRequest$ from 'api/getBreadcrumbContent';
import { store$ } from '@redux/store';
import { filter, map, distinctUntilChanged, switchMap, catchError, of, shareReplay } from 'rxjs';
import { searchParametersSelectors, selectResultType } from 'features/searchResultsFacets';
import objectEquals from 'utils/equals';

const { selectLocation, selectProductType } = searchParametersSelectors;
const breadcrumbListContainer = document.querySelector('[data-breadcrumb-list]');
const breadcrumbJsonLdMarkupContainer = document.querySelector('[data-breadcrumb-json-ld-markup]');

/**
 * isSameLocation: Checks if the search location has been changed or not.
 * @param {Object} previousState Previous store.
 * @param {Object} currentState Current store.
 * @returns A boolean value related to if the current search has the same location than before.
 */
function isSameLocation(previousState, currentState) {
  const previousLocation = selectLocation(previousState);
  const currentLocation = selectLocation(currentState);

  return objectEquals(previousLocation, currentLocation);
}

/**
 * isSameProductType: Checks if the search product type has been changed or not.
 * @param {Object} previousState Previous store.
 * @param {Object} currentState Current store.
 * @returns A boolean value related to if the current search has the same product type than before.
 */
function isSameProductType(previousState, currentState) {
  const previousProductType = selectProductType(previousState);
  const currentProductType = selectProductType(currentState);

  return previousProductType === currentProductType;
}

/**
 * isSameResultType: Checks if the search result type has been changed or not.
 * @param {Object} previousState Previous store.
 * @param {Object} currentState Current store.
 * @returns A boolean value related to if the current search has the same result type than before.
 */
function isSameResultType(previousState, currentState) {
  const previousResultType = selectResultType(previousState);
  const currentResultType = selectResultType(currentState);

  return previousResultType === currentResultType;
}

/**
 * isSameBreadcrumb: Checks if the search should change the breadcrumb or not.
 * @param {Object} previousState Previous store.
 * @param {Object} currentState Current store.
 * @returns A boolean value related to if the current search should have the same breadcrumb than before.
 */
function isSameBreadcrumb(previousState, currentState) {
  return (
    isSameLocation(previousState, currentState) &&
    isSameProductType(previousState, currentState) &&
    isSameResultType(previousState, currentState)
  );
}

const onLocationChange$ = store$.pipe(distinctUntilChanged(isSameBreadcrumb));

const onSeachWithinMap$ = onLocationChange$.pipe(filter((state) => state?.mapState?.isSearchWithinMap));

onSeachWithinMap$.subscribe(() => {
  // We have to remove the breadcrumb when the Search Within Map is active.
  breadcrumbListContainer.innerHTML = '';
});

const onBreadcrumbReady$ = onLocationChange$.pipe(
  filter((state) => !state?.mapState?.isSearchWithinMap),
  switchMap((state) => {
    const payload = {
      location: state?.searchParameters?.location,
      product: state?.searchParameters?.product,
      results: state?.searchParameters?.results,
    };
    return getBreadcrumbContentRequest$(payload).pipe(
      catchError(() => of(null)) // catch and ignore any errors
    );
  }),
  shareReplay(1)
);

/**
 * createRootBreadcrumb: Creates the content for the Root Breadcrumb.
 * The Root Breadcrumb relates to the parts from the breadcrumb section that can be navitated to.
 * For example, 'New Homes' Breadcrumb will always be a Root Breadcrumb and redirects to the homepage.
 * @param {Object} breadcrumb Breadcrumb information.
 * @returns The content for the Root breadcrumb.
 */
function createRootBreadcrumb(breadcrumb) {
  const anchorElement = document.createElement('a');
  anchorElement.classList.add('nhs-c-breadcrumb__link');
  anchorElement.href = breadcrumb.Url;
  anchorElement.title = breadcrumb.Title;
  anchorElement.dataset.breadcrumbRoot = '';

  const spanElement = document.createElement('span');
  spanElement.classList.add('nhs-c-breadcrumb__text');
  spanElement.innerText = breadcrumb.Name;

  anchorElement.append(spanElement);
  return anchorElement;
}

/**
 * createBaseBreadcrumb: Creates the content for the Base Breadcrumb.
 * The Base Breadcrumb relates to the parts of the breadcrumb that cannot be navigated to.
 * For example, the last breadcrumb part will always be a Base breadcrumb because it does not redirect.
 * @param {Object} breadcrumb Breadcrumb information.
 * @returns The content for the Base breadcrumb.
 */
function createBaseBreadcrumb(breadcrumb) {
  const spanElement = document.createElement('span');
  spanElement.classList.add('nhs-c-breadcrumb__text--active');
  spanElement.innerText = breadcrumb.Name;

  return spanElement;
}

/**
 * addBreadcrumbElement: Creates the correct Breadcrumb element with a breadcrumb information.
 * @param {Object} breadcrumb Breadcrumb information.
 */
function createBreadcrumbElement(breadcrumb) {
  const liElement = document.createElement('li');
  liElement.classList.add('nhs-c-breadcrumb__item');
  const createNewBreadcrumbElement = breadcrumb.IsAnchorLink ? createRootBreadcrumb : createBaseBreadcrumb;
  const newBreadcrumbElement = createNewBreadcrumbElement(breadcrumb);
  liElement.append(newBreadcrumbElement);

  return liElement;
}

/**
 * updateBreadcrumbList: Updates a new entire breadcrumb list section.
 * @param {Array} breadcrumbList List of Breadcrumb parts.
 */
function updateBreadcrumbList(breadcrumbList) {
  const currentBreadcrumbElements = breadcrumbListContainer.children;

  // We have to remove the extra breadcrumbs for the actual breadcrumb list.
  // For example, the city breadcrumb section has 4 segments and the market breadcrumb has 3 segments.
  // So when we move from city to market, we have to remove the extra last breadcrumb segment.
  const breadcrumbsToRemove = [...currentBreadcrumbElements].filter(
    (breadcrumb, index) => breadcrumbList.length <= index
  );
  breadcrumbsToRemove.forEach((breadcrumbElement) => breadcrumbElement.remove());

  breadcrumbList.forEach((breadcrumb, index) => {
    const newBreadcrumb = createBreadcrumbElement(breadcrumb);
    const samePositionBreadcrumb = currentBreadcrumbElements[index];
    const isSameBreadcrumbElement = samePositionBreadcrumb && samePositionBreadcrumb.isEqualNode(newBreadcrumb);

    // We only have to modify the breadcrumb segments that are not the same
    // to avoid doing changes on DOM that are not necessary.
    if (isSameBreadcrumbElement) {
      return;
    }

    // If there is a breadcrumb on the same position, we have to replace it with the new one to do not affect the order.
    // Otherwise, we only add the new one at the end.
    if (samePositionBreadcrumb) {
      samePositionBreadcrumb.replaceWith(newBreadcrumb);
    } else {
      breadcrumbListContainer.append(newBreadcrumb);
    }
  });
}

onBreadcrumbReady$
  .pipe(
    map((response) => response.BreadcrumbStack),
    distinctUntilChanged()
  )
  .subscribe(updateBreadcrumbList);

/**
 * updateJsonLdMarkup: Updates the new Json-Ld Markup section of the current breadcrumb.
 * @param {string} breadcrumbJsonLdMarkup Json-Ld Markup information for the current breadcrumb.
 */
function updateJsonLdMarkup(breadcrumbJsonLdMarkup) {
  breadcrumbJsonLdMarkupContainer.innerHTML = breadcrumbJsonLdMarkup;
}

onBreadcrumbReady$
  .pipe(
    map((response) => response.BreadcrumbJsonLdMarkup),
    distinctUntilChanged()
  )
  .subscribe(updateJsonLdMarkup);
